import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { fetchRemindersNeedingReschedule, updateReminder } from "@/lib/api";

// One-at-a-time Mochi-voiced follow-up for reminders that finished the
// auto-snooze ladder without being completed. Appears as a tiny inline
// pill at the top of the home screen on next app open.
//
// Tap "reschedule" → calls onReschedule(reminder) which opens the time
// picker in the caller. Tap "skip" → archives the reminder quietly so it
// never reappears.
export default function RestedReminderPrompt({ open, onReschedule }) {
    const [queue, setQueue] = useState([]);

    // Fetch on mount, and once after every app focus (in case midnight
    // crossed while the tab was open and yesterday's items were archived
    // server-side). Mounts only when `open` is true so the home tab owns
    // when this prompt is allowed to surface.
    useEffect(() => {
        if (!open) return;
        let cancelled = false;
        const load = async () => {
            try {
                const list = await fetchRemindersNeedingReschedule();
                if (!cancelled) setQueue(Array.isArray(list) ? list : []);
            } catch { /* silent — best effort */ }
        };
        load();
        const onFocus = () => load();
        window.addEventListener("focus", onFocus);
        return () => { cancelled = true; window.removeEventListener("focus", onFocus); };
    }, [open]);

    const current = queue[0];
    if (!current) return null;

    const handleSkip = async () => {
        try { await updateReminder(current.id, { archived: true }); } catch { /* noop */ }
        setQueue((q) => q.slice(1));
    };
    const handleReschedule = () => {
        // Hand off to the caller (App.js) — it opens the inline time picker
        // on the actual reminder row via the existing pencil-menu plumbing.
        onReschedule?.(current);
        setQueue((q) => q.slice(1));
    };

    return (
        <AnimatePresence>
            <motion.div
                key={current.id}
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.3, ease: "easeOut" }}
                className="bg-cream rounded-2xl border border-warm-300/40 shadow-clay px-4 py-3 flex items-center gap-3"
                data-testid="rested-reminder-prompt"
            >
                <div className="flex-1 min-w-0">
                    <p className="font-display text-[10px] uppercase tracking-widest text-warm-500">mochi 🍡</p>
                    <p className="font-body text-warm-900 text-sm leading-snug">
                        hey… <span className="font-bold">{current.title}</span> got a little lost today. want to reschedule it?
                    </p>
                </div>
                <div className="shrink-0 flex flex-col gap-1.5">
                    <button
                        type="button"
                        onClick={handleReschedule}
                        className="bg-sage hover:bg-sage-hover text-cream font-display text-xs px-3 py-1.5 rounded-full transition-all active:scale-95"
                        data-testid="rested-reschedule-yes"
                    >
                        yes
                    </button>
                    <button
                        type="button"
                        onClick={handleSkip}
                        className="bg-warm-200/60 hover:bg-warm-300/60 text-warm-900 font-display text-xs px-3 py-1.5 rounded-full transition-all active:scale-95"
                        data-testid="rested-reschedule-no"
                    >
                        no
                    </button>
                </div>
            </motion.div>
        </AnimatePresence>
    );
}
